import { useState } from 'react';
import { Table, Title, Group } from '@mantine/core';
import { MeansCash } from '../../../../libraries/transactions';
import { prepareQuantities } from '../../../utils/script';
import { ICurrency } from '../../../../libraries/currencies';
import { Language } from '../../../languages/languageHandler';
import './styles.scss';

interface CurrencyTableProps {
  lang: Language;
  currency: ICurrency;
  label?: string;
  cash?: MeansCash[];
}

const PeriodCurrencyTable = ({ lang, label, currency, cash }: CurrencyTableProps) => {
  const [nrCoins] = useState<number>(currency.nCoins);
  const [nrBills] = useState<number>(currency.nBills);

  const safeCash = cash ?? [];
  const denominations = prepareQuantities(safeCash, currency);

  const hasCoins = denominations.slice(0, nrCoins).some(denom => denom.amount && denom.amount > 0);
  const hasBills = denominations.slice(nrCoins, nrCoins + nrBills).some(denom => denom.amount && denom.amount > 0);

  const calcCoinsTotalPercentage = (nrCoins: number, nrBills: number) => (93 * nrCoins) / (nrCoins + nrBills);
  const calcBillsTotalPercentage = (nrCoins: number, nrBills: number) => (93 * nrBills) / (nrCoins + nrBills);

  const mapTableHeader = (headerCurrency: ICurrency) => {
    if (!headerCurrency?.denominations) return null;

    const denomsToShow = [
      ...(hasCoins ? headerCurrency.denominations.slice(0, nrCoins) : []),
      ...(hasBills ? headerCurrency.denominations.slice(nrCoins, nrCoins + nrBills) : [])
    ];

    return denomsToShow.map((denomination, index) => (
      <Table.Th
        key={`header-${index}`}
        style={{
          width: `${
            denomination.type === 1
              ? calcCoinsTotalPercentage(nrCoins, nrBills) / nrCoins
              : calcBillsTotalPercentage(nrCoins, nrBills) / nrBills
          }%`
        }}
      >
        {denomination.value * currency.factor}
      </Table.Th>
    ));
  };

  const denomsToShowIndices = [
    ...(hasCoins ? Array.from({ length: nrCoins }, (_, i) => i) : []),
    ...(hasBills ? Array.from({ length: nrBills }, (_, i) => nrCoins + i) : [])
  ];

  return (
    <Group className="period-table">
      {label && <Title order={4}>{label}</Title>}
      {cash && (hasCoins || hasBills) && (
        <Table.ScrollContainer minWidth={500}>
          <Table withColumnBorders withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th rowSpan={2}>{currency.currencyStr}</Table.Th>
                {hasCoins && <Table.Th colSpan={nrCoins}>{lang.GLOBAL_COINS}</Table.Th>}
                {hasBills && <Table.Th colSpan={nrBills}>{lang.GLOBAL_BILLS}</Table.Th>}
              </Table.Tr>
              <Table.Tr>{mapTableHeader(currency)}</Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              <Table.Tr>
                <Table.Th>{lang.GLOBAL_AMOUNT}</Table.Th>
                {denomsToShowIndices.map(i => (
                  <Table.Td key={`amount-${i}`}>{denominations[i]?.amount ?? 0}</Table.Td>
                ))}
              </Table.Tr>
              <Table.Tr>
                <Table.Th>{lang.GLOBAL_QUANTITY}</Table.Th>
                {denomsToShowIndices.map(i => (
                  <Table.Td key={`quantity-${i}`}>{denominations[i]?.quantity ?? 0}</Table.Td>
                ))}
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Group>
  );
};

export default PeriodCurrencyTable;
